/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
We gebruiken geen routing maar ons eigen window management systeem.
Daarom is de appcomponent als startupcomponent de enige. Die heeft het loginscherm en
het windowsysteem.
Wij openen ze op string, omdat de schermklassen zichzelf registreren bij de windowmanager
 */
import {Component, ElementRef, Injector, OnInit, ViewChild} from '@angular/core';
import {Sihwlog} from 'sihw-ngx';
import {ApiService} from '../services/api/api.service';
import {SihwWindowmanager} from 'src/components/windowmanager/windowmanager/windowmanager';
import {LocalConfig} from 'sihw-ngx';
import 'moment/locale/nl.js';
import {NgSelectConfig} from '@ng-select/ng-select';
import {hash} from 'sihw-ngx';
import {Basemodel} from '../models/basemodel';
import {defineLocale} from 'ngx-bootstrap/chronos';
import {nlLocale} from 'ngx-bootstrap/locale';
import {BsLocaleService} from 'ngx-bootstrap/datepicker';
import type {Wachtwoordweergave} from '../components/wachtwoord/wachtwoord.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'ATRPlan';
  @ViewChild('emailveld', {static: false}) emailveld: ElementRef;
  @ViewChild('mgr', {static: false}) windowsmanager: SihwWindowmanager;
  private log: Sihwlog;

  //deel "splash"
  public msg = '';

  public inlogin: boolean = false; //zijn we bezig met login?
  public login = {
    email: (localStorage && localStorage.loginEmail) || '',
    wachtwoord: ''
  }; //het loginmodel.
  public label: string = null; //gezet uit localconfig, wordt dan afgebeeld om de verschillende omgevingen uit elkaar te houden

  //deel "wachtwoord vergeten": staat het wachtwoordscherm in plaats van het loginformulier, en zo ja in welke vorm
  public wachtwoordzichtbaar: boolean = false;
  public wachtwoordweergave: Wachtwoordweergave = 'aanvragen';
  public wachtwoorduid: string = null;
  public wachtwoordcode: string = null;

  private config = new LocalConfig();

  constructor(public api: ApiService, injector: Injector, ngSelectConfig: NgSelectConfig, bsLocaleService: BsLocaleService) {
    this.log = new Sihwlog('debug', 'App');
    this.log.info(`ATRPlan (C) 2022 Slimme Infrasoftware B.V., Apeldoorn, The Netherlands`);

    this.label = this.config.$('label');
    if (this.label)
    {
      document.title += ` ${this.label}`;
    }
    //moment locale hoeft alleen geïmporteerd te worden

    //nxgbootstrap locale
    defineLocale('nl', nlLocale);
    bsLocaleService.use('nl');
    //initialiseer de modellen
    Basemodel.init(injector);

    //ngSelect even op NL
    ngSelectConfig.notFoundText = 'Niet gevonden';
    ngSelectConfig.clearAllText = 'Legen';
    ngSelectConfig.loadingText = 'Laden...';

  }

  public get ingelogd(): boolean {
    return this.api.loggedInSync; //dupliceer de getter
  }

  async ngOnInit() {
    //Raakt de sessie onderweg kwijt, dan hoort het inlogformulier te verschijnen. Dat gebeurt eerder dan je
    //denkt: de authkey staat in localStorage (zie authPersistence in localconfig.json) en is dus gedeeld
    //tussen tabbladen, dus een tabblad dat uitlogt neemt de andere mee. In dít tabblad draait er dan geen
    //enkele regel code — loggedInSync leest die opslag rechtstreeks — en het storage-event is het enige
    //signaal dat we krijgen. Zonder dit blijft er een kale splash staan, of de oude tekst "Verbinden met
    //de server", met een inlogformulier dat er wel is maar onzichtbaar blijft
    window.addEventListener('storage', () => this.controleerSessie());
    this.api.loggedOut$.subscribe(() => this.controleerSessie());
    if (await this.checkWachtwoordlink()) {
      return; //we komen binnen op een resetlink; de rest van het opstarten wacht tot dat scherm klaar is
    }
    await this.checkLoggedIn();
  }

  /**
   * De sessie is mogelijk weggevallen zonder dat dit tabblad er iets voor deed. Is dat zo, dan komt het
   * inlogformulier in beeld; staat het wachtwoordscherm er juist, dan laten we dat met rust
   * @private
   */
  private controleerSessie() {
    if (this.ingelogd || this.wachtwoordzichtbaar) {
      return;
    }
    this.openLogin();
  }

  /**
   * Toon het wachtwoordscherm om een link aan te vragen. Handler voor het linkje onder het loginformulier
   */
  public wachtwoordVergeten() {
    this.wachtwoordweergave = 'aanvragen';
    this.wachtwoordzichtbaar = true;
  }

  /**
   * Het wachtwoordscherm is klaar: bij een sessie door naar de startschermen, anders terug naar het loginformulier
   * @param uitkomst of er een sessie is, en het e-mailadres dat de gebruiker daar gebruikte
   */
  public wachtwoordKlaar(uitkomst: {ingelogd: boolean, email: string}) {
    this.wachtwoordzichtbaar = false;
    this.wachtwoorduid = null;
    this.wachtwoordcode = null;
    if (uitkomst.ingelogd) {
      this.startSchermen();
      return;
    }
    //het adres dat de gebruiker zojuist gebruikte hoort in het inlogformulier te staan, niet dat van de
    //vorige inlog op deze computer
    if (uitkomst.email) {
      this.login.email = uitkomst.email;
    }
    this.openLogin();
  }

  /**
   * Kijk of we binnenkomen op een link uit een wachtwoordmail. Zo ja: uitloggen, het instelformulier tonen en de
   * code uit de adresbalk halen, zodat hij niet in de geschiedenis blijft staan
   * @private
   */
  private async checkWachtwoordlink(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search);
    if (!(params.get('wachtwoord') && params.get('code'))) {
      return false;
    }
    this.wachtwoorduid = params.get('wachtwoord');
    this.wachtwoordcode = params.get('code');
    this.msg = null; //geen splash, we hebben iets te doen
    await this.api.logout(); //een oude sessie mag hier niet meespelen
    history.replaceState(null, '', window.location.pathname);
    this.wachtwoordweergave = 'instellen';
    this.wachtwoordzichtbaar = true;
    return true;
  }

  /**
   * Controleer of de api zover is, en of we zijn ingelogd of niet
   */
  private async checkLoggedIn() {
    //vraag api of we ingelogd zijn. Die wacht zelf wel op zijn init
    this.msg = 'Verbinden met de server...';
    if (await this.api.loggedIn()) {
      this.log.debug(`Api in orde en ingelogd.`);

      //dan komt het maindeel vanzelf naar voren
      this.startSchermen(); //open ingestelde startschermen
    } else {
      this.log.debug(`Niet ingelogd: naar login`);
      this.openLogin();
    }
  }

  /**
   * Open de startschermen uit de config, of anders het planbord
   * @private
   */
  private startSchermen() {
    //speciaal voor e2e users geen startschermen

    let startschermen = this.config.$('startschermen', []);
    //als er startschermen in de localconfig staan, dan is dat voor development. Die skippen we in e2e (de startschermen per usergroep hieronder doen we wél in e2e, want daar willen we op testen)
    if ((! this.api.userdata.gebruikersnaam.match(/^e2e_/)) && startschermen.length) {
      for (let scherm of startschermen) {
        setTimeout(() => {
          if (Array.isArray(scherm)) {
            this.open(scherm[0], scherm[1]);
          } else {
            this.open(scherm);
          }
        }, 200);
      }
    } else {
      //geen startscherm gedefineerd, dan open we eventueel een scherm dat past bij de groep
      const schermpergroep = {
       // admin: 'Planbordscherm',
        planner: 'Planbordscherm',
        aanvrager: 'Planbordscherm',
        leverancier: 'Planbordscherm',
        office: 'Werkbonnenlijstscherm',
        hr: 'Vastpersoneelscherm'
      };
      const scherm = schermpergroep[this.api.userdata?.groep];
      if (scherm) {
        setTimeout(() => {
          this.open(scherm);
        }, 200);
      }
    }
  }

  private openLogin() {
    this.log.debug('openLogin');
    this.msg = null; //dicht
    this.inlogin = true;
    this.log.debug(this.emailveld);
    setTimeout(() => {
      //het veld kan er nog niet zijn als we net terugkomen uit het wachtwoordscherm
      this.emailveld?.nativeElement?.focus();
    }, 10);
  }

  /**
   * Handler voor loginform (public voor prod)
   */
  public async doeLogin() {
    //ux heeft de boel al gecheckt
    try {
      await this.api.login(this.login.email, this.login.wachtwoord);
      localStorage.loginEmail = this.login.email; //bewaren
      //gelukt
      this.api.toast('Welkom bij ATRPlan', 'success');
      this.log.debug(this.api.userdata);
      this.inlogin = false; //zodat hij later weer goed opkomt
      this.login.wachtwoord = ''; //legen
      //open de startschermen
      this.startSchermen();
    } catch (errcode) {
    }
  }

  //menuitems
  /**
   * Open een scherm
   * @param schermnaam De stringvariant van een scherm, gewoon de classnaam als string. De class moet dus geregistreerd zijn bij de windowmanager (Sihwwindowmanager.register).
   * @param data Optioneel de data zoals verwacht door het scherm
   */
  open(schermnaam: string, data: hash<any> = {}) {
    this.windowsmanager.create(schermnaam, data);
  }

  //sluit alles
  closeAll() {
    this.windowsmanager.closeAll();
  }

  async logout() {
    this.windowsmanager.closeAll(); //alles dicht
    await this.api.logout(); //that's it, want dan gaat main weg en komt login terug
    //opnieuw inloggen
    setTimeout(() => {
      void this.checkLoggedIn();
    });
  }

  //exact
  /**
   * Forceer herkoppelen van exact
   */
  async exactKoppelen(): Promise<void> {
    if (await this.api.confirm('Exactkoppeling', 'Deze koppeling voor automatische synchronisatie tussen Exact en ATRPlan, en voor speciale acties zoals factureren en inkoopbonnen maken. Door deze koppeling opnieuw te maken, verbind je ATRPlan aan Exact <em>met jouw toestemmingen</em>. Wil je de koppeling herstellen?')) {
      window.open(await this.api.exactUrl());
    }
  }

  //ux
  /**
   * Lever een hexstring voor de achtergrondkleur van het performanceblokje
   */
  performance_bg(): string {
    if (! this.api.connected) {
      return `rgb(255,0,0)`;
    }
    let perc = Math.min(100, this.api.performancedata?.load ?? 0) / 100; //max 100
    let r = Math.round(255 * perc);
    let g = Math.round(180 - 180 * perc);
    // this.log.debug(`rgb(${r},${g},0)`);
    return `rgb(${r},${g},0)`;
  }

  /**
   *
   * @param id
   */
  async switchWerkmaatschappij(id: number) {
      await this.api.tag('wm',id);
      location.reload();
  }
}
